import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { motion } from "framer-motion";
import { GetImageUrl } from "../../helpers/GetImageUrl";

const ApplicationModal = ({
  handleResolve,
  onClose,
  loading,
  applicationDetail,
}) => {
  const [selectedStatus, setSelectedStatus] = useState("Pending");

  useEffect(() => {
    if (applicationDetail) {
      setSelectedStatus(applicationDetail.status);
    }
  }, [applicationDetail]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  if (!applicationDetail) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5 bg-gray-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Application Details
            </h2>
            <p className="text-sm text-gray-600">View and manage application</p>
          </div>

          <button
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 transition hover:bg-red-500 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {/* Profile */}
        <div className="flex flex-col items-center justify-between gap-6 border-b border-gray-200 px-6 py-6 md:flex-row">
          <div className="flex items-center gap-5">
            <img
              src={GetImageUrl(applicationDetail.profilePicture)}
              alt={applicationDetail.name}
              className="h-32 w-32 rounded-full border object-cover"
            />
          </div>

          {applicationDetail.document && (
            <a
              href={GetImageUrl(applicationDetail.document)}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg bg-blue-600 px-5 py-2 text-white transition hover:bg-blue-700"
            >
              View Document
            </a>
          )}
        </div>

        {/* Details */}
        <div className="grid grid-cols-1 gap-5 px-6 py-6 md:grid-cols-2">
          <Field label="Full Name" value={applicationDetail.name} />
          <Field label="Email" value={applicationDetail.email} />
          <Field label="Phone Number" value={applicationDetail.phoneNumber} />
          <Field label="Address" value={applicationDetail.address} />
          <Field label="Gender" value={applicationDetail.gender} />
          <Field label="Applied For" value={applicationDetail?.career?.title} />
          <Field
            label="Application Type"
            value={applicationDetail.careerType}
          />
          <Field
            label="Qualification"
            value={applicationDetail.qualification}
          />
          <Field
            label="Applied On"
            value={new Date(applicationDetail.createdAt).toLocaleString()}
          />

          {applicationDetail.careerType === "Job" ? (
            <>
              <Field
                label="Experience"
                value={`${applicationDetail.experience} Years`}
              />
              <Field
                label="Current Company"
                value={applicationDetail.currentCompany}
              />
              <Field label="Current CTC" value={applicationDetail.currentCtc} />
              <Field
                label="Expected CTC"
                value={applicationDetail.expectedCtc}
              />
              <Field
                label="Notice Period"
                value={applicationDetail.noticePeriod}
              />
            </>
          ) : (
            <>
              <Field
                label="Current Occupation"
                value={applicationDetail.currentOccupation}
              />
              <Field label="Reason" value={applicationDetail.reason} />
            </>
          )}

          {/* Status */}
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Application Status
            </label>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 focus:border-blue-500 focus:outline-none"
            >
              <option value="Pending">Pending</option>
              <option value="Reviewed">Reviewed</option>
              <option value="Shortlisted">Shortlisted</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-5">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-5 py-2 text-gray-700 transition hover:bg-gray-100"
          >
            Cancel
          </button>

          <button
            onClick={() => handleResolve(applicationDetail._id, selectedStatus)}
            className="rounded-lg bg-blue-600 px-5 py-2 text-white transition hover:bg-blue-700"
          >
            Save Status
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

function Field({ label, value }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-gray-600">
        {label}
      </label>

      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-900 break-words">
        {value || "-"}
      </div>
    </div>
  );
}

export default ApplicationModal;
